import { useRef, useState } from "react";
import { downloadFile } from "src/lib/utils/auto-download";
import { Download, OptionalRefObject } from "src/types";
import { Popup } from "../popup";
import { DownloadCropSelect } from "./download-crop-select";
import { DownloadSourceSelect } from "./download-source-select";
import { DownloadTerms } from "./download-terms";
import { Stepper } from "./stepper";
import { DownloadSubtitleOptionSelect } from "./download-subtitle-option-select";
import { CreateVideoClip } from "../create-video-clip/create-video-clip";
import { SubtitleOption } from "src/models/subtitle-option.model";
import { getEntryIdFromSource } from "src/lib/utils/entry-id-from-source";
import { getKalturaMediaEntry } from "src/api/videos.api";
import { createVideoClip } from "src/api/video-clips.api";
import { getTimeStampParts } from "src/lib/utils/timestamp-parts";
import { Spinner } from "../spinner";
import { serveCaptionAssetByEntryId } from "src/api/caption.api";

interface PlenaryDownloadProps {
  downloads: Download[];
  anchors?: Array<OptionalRefObject> | OptionalRefObject;
  handleCancel?: () => void;
  handleClickOutside?: () => void;
  className?: string;
}

export const LINK_STYLES = "text-red-400 hover:underline focus:underline focus:outline-none";
export const ACTION_BUTTON = "px-7 py-2 disabled:bg-gray-500";

export function PlenaryDownload({
  downloads,
  anchors,
  handleCancel,
  handleClickOutside,
  className = "",
}: PlenaryDownloadProps) {
  const [source, setSource] = useState("");
  const [subtitleOption, setSubtitleOption] = useState<SubtitleOption>(SubtitleOption.None);
  const [showVideoCrop, setShowVideoCrop] = useState(false);
  const [showSubtitlesStep, setShowSubtitlesStep] = useState(false);
  const [loading, setLoading] = useState(false);

  const stepRefs = useRef<OptionalRefObject[]>([]);

  const handleSelectSource = (source: string, hasSubtitles: boolean) => {
    setSource(source);
    setShowSubtitlesStep(hasSubtitles);
  };

  const handleSelectCrop = async (crop: boolean) => {
    if (!crop) {
      setLoading(true);
      // We download the file directly from Kaltura
      // if no clipping or open captions setting is selected.
      if (subtitleOption === SubtitleOption.None) {
        await handleCreateClip(source, null);
        return;
      }

      const entryId = getEntryIdFromSource(source);

      if (subtitleOption === SubtitleOption.Separated) {
        const captionUrl = await serveCaptionAssetByEntryId({ entryIdEqual: entryId })
        await handleCreateClip(source, captionUrl);
        return;
      }

      try {
        const data = await getKalturaMediaEntry(entryId);
        if (!data) {
          return;
        }
        const { h, m, s } = getTimeStampParts(data?.msDuration);
        const { videoUrl, subtitleUrl } = await createVideoClip({
          source,
          start: "00:00:00",
          end: `${h}:${m}:${s}`,
          subtitle_option: subtitleOption,
        });
        checkBlobExists(videoUrl, subtitleUrl)
          .then(() => console.log("Video clip is ready to download."))
          .catch((error) => console.error("Error checking blob", error));
      } catch (error) {
        console.log(error);
        setLoading(false);
      }
    } else {
      setShowVideoCrop(true);
    }
  };

  const handleCreateClip = async (videoUrl: string, subtitleUrl: string) => {
    let { title } = downloads.find((d) => d.url === source)!;
    title = title.replace(/[/\\?%*:|"<>\\. ]/g, "_");
    const downloadFiles = [downloadFile(videoUrl, title)];
    if (subtitleUrl) {
      downloadFiles.push(downloadFile(subtitleUrl, title, 'vtt'));
    }
    await Promise.all(downloadFiles);
    setTimeout(() => {
      handleCancel?.();
    }, 2_000);
  };

  const checkBlobExists = async (videoUrl: string, subtitleUrl: string): Promise<void> => {
    while (true) {
      try {
        const response = await fetch(videoUrl, { method: 'HEAD' });

        if (response.ok) {
          await handleCreateClip(videoUrl, subtitleUrl);
          break
        }
      } catch (error) {
        console.error('Error checking blob existence:', error);
      }

      await new Promise((resolve) => setTimeout(resolve, 5000));
    }
  }

  return (
    <>
      {!showVideoCrop ? (
        <Popup
          className={`${className} overflow-x-hidden`}
          anchors={anchors}
          containers={stepRefs.current}
          handleClickOutside={handleClickOutside}
        >
          <Stepper cancel={handleCancel} stepRefs={stepRefs}>
            <DownloadTerms />
            <DownloadSourceSelect downloadItems={downloads} selectSource={handleSelectSource} />
            {showSubtitlesStep && <DownloadSubtitleOptionSelect selectSubtitleOption={setSubtitleOption} />}
            <DownloadCropSelect selectCrop={handleSelectCrop} />
          </Stepper>
          {loading && (
            <div className="absolute inset-0 p-2 flex flex-col text-center justify-center items-center bg-white motion-safe:animate-fadein">
              <p className="px-3 mb-4">Wir bitten um einen Moment Geduld.<br/>Ihr Download steht in Kürze bereit.</p>
              <Spinner />
            </div>
          )}
        </Popup>
      ) : (
        <Popup
          backdrop={true}
          className="!fixed left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 p-4 w-4/5 max-w-xl bg-white border border-gray-100"
        >
          <CreateVideoClip
            source={source}
            subtitleOption={subtitleOption}
            handleCreateClip={handleCreateClip}
            handleCancel={handleCancel}
          ></CreateVideoClip>
        </Popup>
      )}
    </>
  );
}
